/**
 * TaskIntake (T-039 form mode + T-040 chat mode).
 *
 * Two intake modes that share one submit path:
 *  - Form mode (T-039): textarea + grouped 16-type dropdown + coaching-depth
 *    selector. Submit runs the server-side shape-check (ARCH-009) and renders
 *    a ShapeCheckPanel: tightened phrasing + flagged risks, or a 2-4 child
 *    split proposal the subscriber accepts or overrides.
 *  - Chat mode (T-040): a DETERMINISTIC scripted Q&A (no LLM — backlog
 *    guardrail "DON'T use an LLM to generate the synthesis card in V1"). Chip
 *    quick-answers with a free-text fallback always available; a fixed final
 *    open question; a synthesis card that is a STRUCTURED DISPLAY of the typed
 *    answers (not an LLM summary). Submit goes through the same shape-check +
 *    write path. "Edit in form" switches to form mode pre-populated.
 *
 * ADR-candidate boundary (ARCH-009, T-039): the shape-check LLM call is
 * server-side only and its endpoint + studio-mcp helper are OUTSIDE Phase G
 * file ownership. This component calls the documented endpoint via
 * lib/queries/task-intake.runShapeCheck, which fails open to a soft_warning
 * verdict (ARCH-009 §5) when the endpoint is unavailable. Shape-check is never
 * a hard gate (ARCH-009 §6.2 DON'T) — "Submit anyway" is always present.
 */

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { CoachingDepth, TaskType } from '@studio/api-schemas';
import {
  runShapeCheck,
  writeBacklogTask,
  writeSplitTasks,
  type ShapeCheckProposedChild,
  type ShapeCheckResponse,
} from '@/lib/queries/task-intake';
import { toast } from '@/components/ui/toast';
import { CoachingDepthSelector } from './CoachingDepthSelector';
import { TASK_TYPE_GROUPS, TASK_TYPE_LABEL } from './task-type-labels';

interface TaskIntakeProps {
  subscriberId: string;
  tier: 'studio' | 'pro';
  accessToken: string;
}

type Mode = 'form' | 'chat';

export function TaskIntake({ subscriberId, tier, accessToken }: TaskIntakeProps) {
  const [mode, setMode] = useState<Mode>('form');
  const [brief, setBrief] = useState('');
  const [type, setType] = useState<TaskType>('positioning_clarity');
  const [depth, setDepth] = useState<CoachingDepth>('recommend');

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-text-mute)]">
          New task · shape it for a cycle
        </p>
        <h1 className="mb-1 font-display text-h2 italic leading-tight text-[color:var(--color-navy)]">
          What are we trying to figure out?
        </h1>
        <p className="text-sm text-[color:var(--color-text-mute)]">
          Tell me what you need. I will tighten it with you before it goes in
          the backlog.
        </p>
      </div>

      <div
        className="inline-flex gap-1 rounded-md border border-[color:var(--color-line-dark)] bg-[color:var(--color-linen)] p-1"
        role="tablist"
        aria-label="Intake mode"
      >
        {(['form', 'chat'] as const).map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            onClick={() => setMode(m)}
            className={[
              'rounded-sm px-3.5 py-1.5 text-sm font-medium transition-colors',
              mode === m
                ? 'bg-[color:var(--color-white)] text-[color:var(--color-navy)] shadow-[var(--shadow-sm)]'
                : 'text-[color:var(--color-text-mute)] hover:text-[color:var(--color-navy)]',
            ].join(' ')}
          >
            {m === 'form' ? 'Write it' : 'Talk it through'}
          </button>
        ))}
      </div>

      {mode === 'form' ? (
        <FormIntake
          subscriberId={subscriberId}
          tier={tier}
          accessToken={accessToken}
          brief={brief}
          setBrief={setBrief}
          type={type}
          setType={setType}
          depth={depth}
          setDepth={setDepth}
        />
      ) : (
        <ChatIntake
          type={type}
          setType={setType}
          onEditInForm={(composedBrief) => {
            setBrief(composedBrief);
            setMode('form');
          }}
          subscriberId={subscriberId}
          tier={tier}
          accessToken={accessToken}
          depth={depth}
          setDepth={setDepth}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Form mode (T-039)                                                         */
/* ------------------------------------------------------------------------ */

function FormIntake(props: {
  subscriberId: string;
  tier: 'studio' | 'pro';
  accessToken: string;
  brief: string;
  setBrief: (s: string) => void;
  type: TaskType;
  setType: (t: TaskType) => void;
  depth: CoachingDepth;
  setDepth: (d: CoachingDepth) => void;
}) {
  const {
    subscriberId,
    tier,
    accessToken,
    brief,
    setBrief,
    type,
    setType,
    depth,
    setDepth,
  } = props;
  const navigate = useNavigate();
  const [phase, setPhase] = useState<'edit' | 'checking' | 'review'>('edit');
  const [verdict, setVerdict] = useState<ShapeCheckResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleShapeCheck() {
    if (brief.trim().length === 0) return;
    setPhase('checking');
    const result = await runShapeCheck(accessToken, {
      brief: brief.trim(),
      task_type: type,
      coaching_depth: depth,
      tier,
    });
    setVerdict(result);
    setPhase('review');
  }

  async function finalize(opts: {
    useBrief: string;
    split?: ShapeCheckProposedChild[];
  }) {
    setSubmitting(true);
    try {
      if (opts.split && opts.split.length >= 2) {
        await writeSplitTasks({
          subscriberId,
          parentBrief: brief.trim(),
          parentType: type,
          intakeMode: 'form',
          coachingDepth: depth,
          children: opts.split,
        });
        toast.success('Split into smaller tasks and added to your backlog.');
      } else {
        await writeBacklogTask({
          subscriberId,
          brief: opts.useBrief,
          type,
          intakeMode: 'form',
          coachingDepth: depth,
        });
        toast.success('Added to your backlog.');
      }
      await navigate({ to: '/portal' });
    } catch {
      toast.error('Could not save the task. Try again in a moment.');
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <label
          htmlFor="task-brief"
          className="mb-1.5 block text-sm font-medium text-[color:var(--color-navy)]"
        >
          What do you need?
        </label>
        <textarea
          id="task-brief"
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          rows={6}
          maxLength={4000}
          placeholder="The more honest the better. What are you actually trying to figure out?"
          className="w-full rounded-md border border-[color:var(--color-line-dark)] bg-[color:var(--color-white)] p-3 text-sm leading-relaxed text-[color:var(--color-navy)] transition-colors focus:border-[color:var(--color-navy)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-rose)]"
        />
      </div>

      <div>
        <label
          htmlFor="task-type"
          className="mb-1.5 block text-sm font-medium text-[color:var(--color-navy)]"
        >
          What kind of task is this?
        </label>
        <select
          id="task-type"
          value={type}
          onChange={(e) => setType(e.target.value as TaskType)}
          className="w-full rounded-md border border-[color:var(--color-line-dark)] bg-[color:var(--color-white)] p-2.5 text-sm text-[color:var(--color-navy)] transition-colors focus:border-[color:var(--color-navy)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-rose)]"
        >
          {TASK_TYPE_GROUPS.map((g) => (
            <optgroup key={g.group} label={g.group}>
              {g.types.map((t) => (
                <option key={t} value={t}>
                  {TASK_TYPE_LABEL[t]}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <CoachingDepthSelector value={depth} onChange={setDepth} />

      {phase === 'edit' || phase === 'checking' ? (
        <button
          type="button"
          onClick={handleShapeCheck}
          disabled={brief.trim().length === 0 || phase === 'checking'}
          className="inline-flex items-center justify-center rounded-sm bg-[color:var(--color-rose)] px-5 py-2.5 text-sm font-medium text-[color:var(--color-navy)] hover:opacity-90 disabled:opacity-50"
        >
          {phase === 'checking' ? 'Checking the shape...' : 'Check the shape'}
        </button>
      ) : null}

      {phase === 'review' && verdict ? (
        <ShapeCheckPanel
          verdict={verdict}
          originalBrief={brief.trim()}
          submitting={submitting}
          onUseTightened={(b) => finalize({ useBrief: b })}
          onUseOriginal={() => finalize({ useBrief: brief.trim() })}
          onAcceptSplit={(children) =>
            finalize({ useBrief: brief.trim(), split: children })
          }
          onBackToEdit={() => setPhase('edit')}
        />
      ) : null}
    </div>
  );
}

function ShapeCheckPanel({
  verdict,
  originalBrief,
  submitting,
  onUseTightened,
  onUseOriginal,
  onAcceptSplit,
  onBackToEdit,
}: {
  verdict: ShapeCheckResponse;
  originalBrief: string;
  submitting: boolean;
  onUseTightened: (brief: string) => void;
  onUseOriginal: () => void;
  onAcceptSplit: (children: ShapeCheckProposedChild[]) => void;
  onBackToEdit: () => void;
}) {
  const isSplit =
    verdict.verdict === 'split' &&
    verdict.proposed_split &&
    verdict.proposed_split.length >= 2;

  return (
    <div className="space-y-4 rounded-lg border border-[color:var(--color-rose)] bg-[color:var(--color-rose-subtle2)] p-5">
      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-rose-dark)]">
        Shape check
      </p>
      {verdict.flagged_risks.length > 0 ? (
        <div>
          <p className="mb-1.5 text-sm font-medium text-[color:var(--color-navy)]">
            What I would watch out for
          </p>
          <ul className="space-y-1 text-sm text-[color:var(--color-text-mute)]">
            {verdict.flagged_risks.map((r, i) => (
              <li key={i}>{r.message}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {isSplit && verdict.proposed_split ? (
        <div>
          <p className="mb-2 text-sm font-medium text-[color:var(--color-navy)]">
            This is bigger than one cycle. I would split it:
          </p>
          <ol className="mb-3 space-y-2">
            {verdict.proposed_split.map((c, i) => (
              <li
                key={i}
                className="rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-white)] p-3"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[color:var(--color-text-mute)]">
                  {TASK_TYPE_LABEL[c.task_type]}
                </p>
                <p className="text-sm font-medium text-[color:var(--color-navy)]">
                  {c.title}
                </p>
                <p className="text-sm text-[color:var(--color-text-mute)]">
                  {c.brief}
                </p>
              </li>
            ))}
          </ol>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              disabled={submitting}
              onClick={() => onAcceptSplit(verdict.proposed_split as ShapeCheckProposedChild[])}
              className="inline-flex items-center justify-center rounded-sm bg-[color:var(--color-rose)] px-4 py-2 text-sm font-medium text-[color:var(--color-navy)] hover:opacity-90 disabled:opacity-50"
            >
              Accept the split
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={onUseOriginal}
              className="inline-flex items-center justify-center rounded-sm border border-[color:var(--color-rule)] bg-[color:var(--color-white)] px-4 py-2 text-sm font-medium text-[color:var(--color-navy)] hover:bg-[color:var(--color-white)]/70 disabled:opacity-50"
            >
              Keep it as one task
            </button>
          </div>
        </div>
      ) : (
        <div>
          {verdict.tightened_brief ? (
            <>
              <p className="mb-1.5 text-sm font-medium text-[color:var(--color-navy)]">
                Tightened
              </p>
              <p className="mb-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-white)] p-3 text-sm text-[color:var(--color-navy)]">
                {verdict.tightened_brief}
              </p>
            </>
          ) : (
            <p className="mb-3 text-sm text-[color:var(--color-text-mute)]">
              This reads clearly. Ready when you are.
            </p>
          )}
          <div className="flex flex-wrap gap-3">
            {verdict.tightened_brief ? (
              <button
                type="button"
                disabled={submitting}
                onClick={() =>
                  onUseTightened(verdict.tightened_brief as string)
                }
                className="inline-flex items-center justify-center rounded-sm bg-[color:var(--color-rose)] px-4 py-2 text-sm font-medium text-[color:var(--color-navy)] hover:opacity-90 disabled:opacity-50"
              >
                Use the tightened version
              </button>
            ) : null}
            <button
              type="button"
              disabled={submitting}
              onClick={onUseOriginal}
              className="inline-flex items-center justify-center rounded-sm border border-[color:var(--color-rule)] bg-[color:var(--color-white)] px-4 py-2 text-sm font-medium text-[color:var(--color-navy)] hover:bg-[color:var(--color-white)]/70 disabled:opacity-50"
            >
              {verdict.tightened_brief
                ? 'Submit my version anyway'
                : 'Add to backlog'}
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={onBackToEdit}
              className="inline-flex items-center justify-center rounded-sm px-3 py-2 text-sm text-[color:var(--color-text-mute)] hover:text-[color:var(--color-navy)] disabled:opacity-50"
            >
              Keep editing
            </button>
          </div>
        </div>
      )}
      {originalBrief.length === 0 ? null : null}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Chat mode (T-040) — deterministic, no LLM                                 */
/* ------------------------------------------------------------------------ */

interface ScriptedQuestion {
  id: string;
  prompt: string;
  chips: ReadonlyArray<string>;
}

/**
 * Deterministic scripted question tree. Type selection first, then a small
 * fixed set of tightening prompts. Chips are quick answers; free text is
 * always available. The FINAL question is the literal required by the
 * T-040 AC.
 */
const FINAL_QUESTION =
  "Anything else you'd like to add or that you think we should know?";

const TIGHTENING_QUESTIONS: ReadonlyArray<ScriptedQuestion> = [
  {
    id: 'outcome',
    prompt: 'What does a good outcome look like for you here?',
    chips: [
      'A clear recommendation I can act on',
      'Something I can show my team',
      'A document I can reuse',
    ],
  },
  {
    id: 'tried',
    prompt: 'What have you already tried or ruled out?',
    chips: ['Nothing yet', 'A few internal attempts', 'Something that failed'],
  },
  {
    id: 'constraint',
    prompt: 'Is there a constraint I should respect?',
    chips: ['Timing', 'Budget', 'A specific audience', 'No hard constraints'],
  },
];

function ChatIntake(props: {
  type: TaskType;
  setType: (t: TaskType) => void;
  onEditInForm: (composedBrief: string) => void;
  subscriberId: string;
  tier: 'studio' | 'pro';
  accessToken: string;
  depth: CoachingDepth;
  setDepth: (d: CoachingDepth) => void;
}) {
  const {
    type,
    setType,
    onEditInForm,
    subscriberId,
    tier,
    accessToken,
    depth,
    setDepth,
  } = props;
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 = type, 1..N = tightening, N+1 = final, N+2 = synthesis
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [finalAnswer, setFinalAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const totalTightening = TIGHTENING_QUESTIONS.length;
  const synthesisStep = 1 + totalTightening + 1; // type + tightening + final

  function composeBrief(): string {
    const lines: string[] = [];
    for (const q of TIGHTENING_QUESTIONS) {
      const a = answers[q.id];
      if (a && a.trim()) lines.push(`${q.prompt}\n${a.trim()}`);
    }
    if (finalAnswer.trim()) {
      lines.push(`${FINAL_QUESTION}\n${finalAnswer.trim()}`);
    }
    return lines.join('\n\n');
  }

  async function submit() {
    setSubmitting(true);
    const composed = composeBrief();
    try {
      // Same shape-check + write path as form mode (T-040 AC). Chat mode does
      // not render the shape-check verdict UI (the synthesis card is the chat
      // confirmation surface); the verdict is still run for telemetry/tighten
      // parity, but submission proceeds regardless (never a hard gate).
      await runShapeCheck(accessToken, {
        brief: composed || '(no additional detail provided)',
        task_type: type,
        coaching_depth: depth,
        tier,
      });
      await writeBacklogTask({
        subscriberId,
        brief: composed || '(submitted via chat with no additional detail)',
        type,
        intakeMode: 'chat',
        coachingDepth: depth,
      });
      toast.success('Added to your backlog.');
      await navigate({ to: '/portal' });
    } catch {
      toast.error('Could not save the task. Try again in a moment.');
      setSubmitting(false);
    }
  }

  // Step 0: type selection
  if (step === 0) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-[color:var(--color-navy)]">
          Let us start simple. What kind of task is this?
        </p>
        <select
          aria-label="Task type"
          value={type}
          onChange={(e) => setType(e.target.value as TaskType)}
          className="w-full rounded-md border border-[color:var(--color-line-dark)] bg-[color:var(--color-white)] p-2.5 text-sm text-[color:var(--color-navy)] transition-colors focus:border-[color:var(--color-navy)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-rose)]"
        >
          {TASK_TYPE_GROUPS.map((g) => (
            <optgroup key={g.group} label={g.group}>
              {g.types.map((t) => (
                <option key={t} value={t}>
                  {TASK_TYPE_LABEL[t]}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setStep(1)}
          className="inline-flex items-center justify-center rounded-sm bg-[color:var(--color-rose)] px-4 py-2 text-sm font-medium text-[color:var(--color-navy)] hover:opacity-90"
        >
          Next
        </button>
      </div>
    );
  }

  // Steps 1..N: tightening questions
  if (step >= 1 && step <= totalTightening) {
    const q = TIGHTENING_QUESTIONS[step - 1];
    if (q === undefined) return null;
    const current = answers[q.id] ?? '';
    return (
      <div className="space-y-4">
        <ProgressDots total={totalTightening + 1} current={step - 1} />
        <p className="text-sm font-medium text-[color:var(--color-navy)]">
          {q.prompt}
        </p>
        <div className="flex flex-wrap gap-2">
          {q.chips.map((chip) => (
            <button
              key={chip}
              type="button"
              onClick={() =>
                setAnswers((a) => ({ ...a, [q.id]: chip }))
              }
              className={[
                'rounded-full border-[1.5px] px-3.5 py-1.5 text-sm transition-colors',
                current === chip
                  ? 'border-[color:var(--color-navy)] bg-[color:var(--color-navy)] text-[color:var(--color-linen)]'
                  : 'border-[color:var(--color-line-dark)] bg-[color:var(--color-white)] text-[color:var(--color-navy)] hover:bg-[color:var(--color-linen)]',
              ].join(' ')}
            >
              {chip}
            </button>
          ))}
        </div>
        <textarea
          aria-label={`${q.prompt} (free text)`}
          value={current}
          onChange={(e) =>
            setAnswers((a) => ({ ...a, [q.id]: e.target.value }))
          }
          rows={3}
          placeholder="Or say it in your own words"
          className="w-full rounded-md border border-[color:var(--color-line-dark)] bg-[color:var(--color-white)] p-3 text-sm leading-relaxed text-[color:var(--color-navy)] transition-colors focus:border-[color:var(--color-navy)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-rose)]"
        />
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setStep(step + 1)}
            className="inline-flex items-center justify-center rounded-sm bg-[color:var(--color-rose)] px-4 py-2 text-sm font-medium text-[color:var(--color-navy)] hover:opacity-90"
          >
            Next
          </button>
          <button
            type="button"
            onClick={() => setStep(step + 1)}
            className="inline-flex items-center justify-center rounded-sm px-3 py-2 text-sm text-[color:var(--color-text-mute)] hover:text-[color:var(--color-navy)]"
          >
            Skip
          </button>
        </div>
      </div>
    );
  }

  // Final open question
  if (step === 1 + totalTightening) {
    return (
      <div className="space-y-4">
        <ProgressDots total={totalTightening + 1} current={totalTightening} />
        <p className="text-sm font-medium text-[color:var(--color-navy)]">
          {FINAL_QUESTION}
        </p>
        <textarea
          aria-label={FINAL_QUESTION}
          value={finalAnswer}
          onChange={(e) => setFinalAnswer(e.target.value)}
          rows={4}
          className="w-full rounded-md border border-[color:var(--color-line-dark)] bg-[color:var(--color-white)] p-3 text-sm leading-relaxed text-[color:var(--color-navy)] transition-colors focus:border-[color:var(--color-navy)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-rose)]"
        />
        <button
          type="button"
          onClick={() => setStep(synthesisStep)}
          className="inline-flex items-center justify-center rounded-sm bg-[color:var(--color-rose)] px-4 py-2 text-sm font-medium text-[color:var(--color-navy)] hover:opacity-90"
        >
          Review
        </button>
      </div>
    );
  }

  // Synthesis card — STRUCTURED display of typed answers (no LLM, per
  // T-040 guardrail).
  return (
    <div className="space-y-5">
      <CoachingDepthSelector value={depth} onChange={setDepth} />
      <div className="rounded-lg border-[1.5px] border-[color:var(--color-navy)] bg-[color:var(--color-white)] p-5">
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-text-mute)]">
          Task draft
        </p>
        <p className="mb-4 text-sm font-medium text-[color:var(--color-navy)]">
          Here is what I have. Submit it, or edit it as a form.
        </p>
        <dl className="space-y-3 text-sm">
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[color:var(--color-text-mute)]">
              Task type
            </dt>
            <dd className="mt-0.5 text-[color:var(--color-navy)]">
              {TASK_TYPE_LABEL[type]}
            </dd>
          </div>
          {TIGHTENING_QUESTIONS.map((q) =>
            answers[q.id]?.trim() ? (
              <div key={q.id}>
                <dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[color:var(--color-text-mute)]">
                  {q.prompt}
                </dt>
                <dd className="mt-0.5 whitespace-pre-line text-[color:var(--color-navy)]">
                  {answers[q.id]}
                </dd>
              </div>
            ) : null,
          )}
          {finalAnswer.trim() ? (
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[color:var(--color-text-mute)]">
                {FINAL_QUESTION}
              </dt>
              <dd className="whitespace-pre-line text-[color:var(--color-navy)]">
                {finalAnswer}
              </dd>
            </div>
          ) : null}
        </dl>
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          disabled={submitting}
          onClick={submit}
          className="inline-flex items-center justify-center rounded-sm bg-[color:var(--color-rose)] px-5 py-2.5 text-sm font-medium text-[color:var(--color-navy)] hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? 'Adding...' : 'Add to backlog'}
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={() => onEditInForm(composeBrief())}
          className="inline-flex items-center justify-center rounded-sm border border-[color:var(--color-rule)] bg-[color:var(--color-white)] px-4 py-2 text-sm font-medium text-[color:var(--color-navy)] hover:bg-[color:var(--color-white)]/70 disabled:opacity-50"
        >
          Edit in form
        </button>
      </div>
    </div>
  );
}

function ProgressDots({
  total,
  current,
}: {
  total: number;
  current: number;
}) {
  return (
    <div className="flex gap-1.5" aria-hidden="true">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={[
            'h-1.5 w-6 rounded-full',
            i <= current
              ? 'bg-[color:var(--color-rose)]'
              : 'bg-[color:var(--color-linen)]',
          ].join(' ')}
        />
      ))}
    </div>
  );
}
